import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2, AlertTriangle, CheckCircle2, Star, MessageSquareQuote } from 'lucide-react'
import { api } from '../lib/api.js'

const REDIRECT_TO = 'https://www.toothtaxi.co'

export default function CstSurvey() {
  const { token } = useParams()
  const [boot, setBoot]   = useState(null)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone]   = useState(false)

  const [scores, setScores] = useState([0, 0, 0, 0, 0, 0])
  const [note,   setNote]   = useState('')

  useEffect(() => {
    api.publicSurvey.cst.get(token).then(setBoot).catch((e) => setError(extractErr(e)))
  }, [token])

  useEffect(() => {
    if (!done) return
    const t = setTimeout(() => { window.location.href = REDIRECT_TO }, 2800)
    return () => clearTimeout(t)
  }, [done])

  async function submit(e) {
    e.preventDefault()
    setError(null)
    if (scores.some((s) => s < 1 || s > 5)) return setError('กรุณาให้คะแนนทุกข้อ')
    setSubmitting(true)
    try {
      await api.publicSurvey.cst.submit(token, {
        q1: scores[0], q2: scores[1], q3: scores[2], q4: scores[3], q5: scores[4], q6: scores[5],
        suggestion: note.trim() || undefined,
      })
      setDone(true)
    } catch (e) {
      setError(extractErr(e))
    } finally {
      setSubmitting(false)
    }
  }

  if (error && !boot) return <Shell><ErrorCard message={error} /></Shell>
  if (!boot)          return <Shell><Centered><Loader2 className="animate-spin text-amber-400" /></Centered></Shell>
  if (done)           return <Shell><ThankYou /></Shell>

  const answered = scores.filter((s) => s >= 1).length
  const progress = Math.round((answered / scores.length) * 100)

  return (
    <Shell>
      <Hero />

      <form onSubmit={submit} className="mt-6 bg-white rounded-3xl shadow-[0_24px_60px_-24px_rgba(217,119,6,0.25)] ring-1 ring-amber-100/70 p-6 md:p-8 space-y-6">
        <div className="text-center pb-1">
          <h1 className="text-[22px] md:text-[26px] font-bold text-zinc-900 tracking-tight">
            แบบประเมินความพึงพอใจ
          </h1>
          <p className="mt-2 text-[13.5px] text-zinc-600 leading-relaxed max-w-[480px] mx-auto">
            We're determined to be the best mobile dental experience for you —
            your feedback is vital to us.
          </p>
          <p className="mt-2 text-[12px] text-zinc-400">1 ดาว (น้อย) ถึง 5 ดาว (มาก)</p>
        </div>

        {/* Progress meter */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-[11.5px] text-zinc-500">
            <span>ตอบแล้ว <span className="text-amber-700 font-semibold">{answered}</span> / {scores.length} ข้อ</span>
            <span className="tabular-nums text-zinc-400">{progress}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <ol className="space-y-4">
          {boot.questions.map((q, i) => (
            <QuestionCard key={i}
              index={i + 1}
              text={q}
              value={scores[i]}
              onChange={(v) => { const next = scores.slice(); next[i] = v; setScores(next) }}
            />
          ))}
        </ol>

        <div>
          <label className="block">
            <span className="inline-flex items-center gap-1.5 mb-1.5 text-[12px] font-semibold text-zinc-700 px-0.5">
              <MessageSquareQuote size={13} className="text-amber-500" /> คำแนะนำเพิ่มเติม
              <span className="ml-1 text-[11px] text-zinc-400 font-normal">(ไม่บังคับ)</span>
            </span>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000}
              placeholder="บอกเล่าประสบการณ์หรือสิ่งที่อยากให้เราปรับปรุง"
              className="w-full px-3.5 py-3 bg-zinc-50/70 rounded-xl text-[14.5px] text-zinc-900 placeholder:text-zinc-400 ring-1 ring-zinc-200 focus:bg-white focus:ring-2 focus:ring-amber-400 focus:outline-none resize-y transition-all" />
          </label>
        </div>

        {error && (
          <div className="px-3.5 py-2.5 rounded-xl bg-rose-50 ring-1 ring-rose-200/80 text-rose-700 text-[13px] flex items-center gap-2">
            <AlertTriangle size={14} className="shrink-0" /> {error}
          </div>
        )}

        <button type="submit" disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-[15px] font-semibold shadow-[0_10px_24px_-8px_rgba(249,115,22,0.55)] transition-all active:scale-[0.99] disabled:opacity-60">
          {submitting
            ? <Loader2 size={16} className="animate-spin" />
            : <span className="text-[15px]">ส่งแบบประเมิน</span>}
        </button>
      </form>

      <Footer />
    </Shell>
  )
}


/* ──────────────────────────────── pieces ──────────────────────────────── */

function QuestionCard({ index, text, value, onChange }) {
  return (
    <li className="rounded-2xl bg-gradient-to-br from-amber-50/40 to-orange-50/20 ring-1 ring-amber-100/70 p-4 md:p-5">
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-7 h-7 rounded-full bg-white ring-1 ring-amber-200 text-amber-700 inline-flex items-center justify-center text-[12.5px] font-bold tabular-nums">
          {index}
        </span>
        <p className="text-[14.5px] text-zinc-900 leading-snug mt-0.5">{text}</p>
      </div>
      <div className="mt-3 ml-10">
        <StarRow value={value} onChange={onChange} />
      </div>
    </li>
  )
}

function StarRow({ value, onChange }) {
  const [hover, setHover] = useState(0)
  const shown = hover || value
  return (
    <div className="inline-flex items-center gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => {
        const active = n <= shown
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            aria-label={`${n} stars`}
            className={'w-10 h-10 rounded-xl inline-flex items-center justify-center transition-all active:scale-95 ' +
              (active
                ? 'text-amber-500 bg-amber-100/70 hover:bg-amber-100'
                : 'text-zinc-300 hover:text-amber-400 hover:bg-zinc-50')}
          >
            <Star size={22} strokeWidth={1.75} fill={active ? 'currentColor' : 'none'} />
          </button>
        )
      })}
      {value > 0 && (
        <span className="ml-2 text-[12px] text-zinc-500 tabular-nums min-w-[28px]">
          {value}/5
        </span>
      )}
    </div>
  )
}

function Hero() {
  return (
    <div className="relative overflow-hidden rounded-3xl shadow-[0_24px_60px_-30px_rgba(217,119,6,0.45)]">
      <img
        src="/hero-cst.png"
        alt="Tooth Taxi — Customer Satisfaction Survey"
        className="w-full h-auto block"
        loading="eager"
      />
    </div>
  )
}

function ThankYou() {
  return (
    <div className="bg-white rounded-3xl shadow-[0_24px_60px_-24px_rgba(249,115,22,0.35)] ring-1 ring-amber-100 p-10 text-center">
      <div className="mx-auto w-16 h-16 rounded-full bg-amber-50 ring-8 ring-amber-50/60 flex items-center justify-center">
        <CheckCircle2 size={36} className="text-amber-500" strokeWidth={2.25} />
      </div>
      <h2 className="mt-4 text-[22px] font-bold text-zinc-900">ขอบคุณสำหรับความเห็น</h2>
      <p className="mt-1.5 text-[13.5px] text-zinc-500">เราจะนำไปปรับปรุงบริการครั้งต่อไป</p>
      <div className="mt-6 inline-flex items-center gap-1.5 text-[11.5px] text-zinc-400">
        <Loader2 size={11} className="animate-spin" />
        กำลังพากลับไปยัง toothtaxi.co…
      </div>
    </div>
  )
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50/40 to-white">
      <div className="max-w-[600px] mx-auto px-4 py-6 md:py-10">{children}</div>
    </div>
  )
}

function Centered({ children }) {
  return <div className="min-h-[60vh] flex items-center justify-center">{children}</div>
}

function ErrorCard({ message }) {
  return (
    <div className="bg-white rounded-3xl ring-1 ring-rose-200/60 shadow-xl p-8 text-center">
      <AlertTriangle size={36} className="mx-auto text-rose-500" />
      <h2 className="mt-3 text-[18px] font-semibold text-zinc-900">ลิงก์นี้ใช้งานไม่ได้</h2>
      <p className="mt-1.5 text-[13px] text-zinc-500">{message}</p>
    </div>
  )
}

function Footer() {
  return (
    <p className="text-center text-[11px] text-zinc-400 mt-5">
      Powered by <span className="font-semibold text-amber-700">Tooth Taxi</span> · Dental Service on the Go
    </p>
  )
}

function extractErr(e) {
  const m = (e?.message || '').match(/:\s*\{?"?error"?"?:\s*"?([^"}\n]+)/)
  return m ? m[1] : (e?.message || 'error')
}
